import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';

const ChemicalUsageTracking = () => {
  const [selectedPeriod, setSelectedPeriod] = useState('current-season');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const periodOptions = [
    { value: 'current-season', label: 'Current Season' },
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-90-days', label: 'Last 90 Days' },
    { value: 'year-to-date', label: 'Year to Date' }
  ];

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'herbicide', label: 'Herbicides' },
    { value: 'insecticide', label: 'Insecticides' },
    { value: 'fungicide', label: 'Fungicides' },
    { value: 'organic', label: 'Organic Approved' }
  ];

  const chemicalUsageData = [
    {
      id: 1,
      name: "Glyphosate 41%",
      category: "herbicide",
      totalUsed: 245.5,
      maxAllowed: 300.0,
      unit: "gallons",
      usagePercentage: 82,
      status: "warning",
      applications: 12,
      lastApplication: "2024-11-14",
      restrictionLevel: "moderate",
      epaNumber: "524-308",
      organicApproved: false
    },
    {
      id: 2,
      name: "Chlorpyrifos 4E",
      category: "insecticide",
      totalUsed: 89.2,
      maxAllowed: 100.0,
      unit: "gallons",
      usagePercentage: 89,
      status: "critical",
      applications: 8,
      lastApplication: "2024-11-15",
      restrictionLevel: "high",
      epaNumber: "62719-324",
      organicApproved: false
    },
    {
      id: 3,
      name: "Copper Sulfate",
      category: "fungicide",
      totalUsed: 1250.0,
      maxAllowed: 2000.0,
      unit: "pounds",
      usagePercentage: 63,
      status: "compliant",
      applications: 15,
      lastApplication: "2024-11-13",
      restrictionLevel: "low",
      epaNumber: "7173-84",
      organicApproved: true
    },
    {
      id: 4,
      name: "Bacillus thuringiensis",
      category: "insecticide",
      totalUsed: 45.8,
      maxAllowed: 100.0,
      unit: "pounds",
      usagePercentage: 46,
      status: "compliant",
      applications: 6,
      lastApplication: "2024-11-12",
      restrictionLevel: "low",
      epaNumber: "73049-39",
      organicApproved: true
    },
    {
      id: 5,
      name: "Imidacloprid 2F",
      category: "insecticide",
      totalUsed: 28.5,
      maxAllowed: 30.0,
      unit: "gallons",
      usagePercentage: 95,
      status: "critical",
      applications: 4,
      lastApplication: "2024-11-11",
      restrictionLevel: "high",
      epaNumber: "432-1318",
      organicApproved: false
    }
  ];

  const getStatusConfig = (status) => {
    switch (status) {
      case 'compliant':
        return {
          color: 'text-success',
          bg: 'bg-success',
          bgLight: 'bg-success/10',
          icon: 'CheckCircle'
        };
      case 'warning':
        return {
          color: 'text-warning',
          bg: 'bg-warning',
          bgLight: 'bg-warning/10',
          icon: 'AlertTriangle'
        };
      case 'critical':
        return {
          color: 'text-error',
          bg: 'bg-error',
          bgLight: 'bg-error/10',
          icon: 'XCircle'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bg: 'bg-muted',
          bgLight: 'bg-muted/10',
          icon: 'Circle'
        };
    }
  };

  const getRestrictionColor = (level) => {
    switch (level) {
      case 'high':
        return 'text-error';
      case 'moderate':
        return 'text-warning';
      case 'low':
        return 'text-success';
      default:
        return 'text-muted-foreground';
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'herbicide':
        return 'Sprout';
      case 'insecticide':
        return 'Bug';
      case 'fungicide':
        return 'Shield';
      default:
        return 'Droplets';
    }
  };

  const filteredData = chemicalUsageData?.filter(item => {
    if (selectedCategory === 'all') return true;
    if (selectedCategory === 'organic') return item?.organicApproved;
    return item?.category === selectedCategory;
  });

  const totalUsageByCategory = filteredData?.reduce((acc, item) => {
    const category = item?.category;
    if (!acc?.[category]) {
      acc[category] = { used: 0, max: 0, count: 0 };
    }
    acc[category].used += item?.totalUsed;
    acc[category].max += item?.maxAllowed;
    acc[category].count += 1;
    return acc;
  }, {});

  return (
    <div className="bg-card rounded-lg border border-border p-6 agricultural-shadow">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 space-y-4 lg:space-y-0">
        <h3 className="text-lg font-semibold text-foreground">Chemical Usage Tracking</h3>
        
        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4">
          <Select
            options={periodOptions}
            value={selectedPeriod}
            onChange={setSelectedPeriod}
            className="w-full sm:w-48"
          />
          <Select
            options={categoryOptions}
            value={selectedCategory}
            onChange={setSelectedCategory}
            className="w-full sm:w-48"
          />
        </div>
      </div>
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {Object.entries(totalUsageByCategory)?.map(([category, data]) => {
          const percentage = Math.round((data?.used / data?.max) * 100);
          const status = percentage >= 90 ? 'critical' : percentage >= 75 ? 'warning' : 'compliant';
          const config = getStatusConfig(status);
          
          return (
            <div key={category} className={`p-4 rounded-lg ${config?.bgLight} border border-border`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center space-x-2">
                  <Icon name={getCategoryIcon(category)} size={20} className={config?.color} />
                  <span className="font-medium text-foreground capitalize">{category}s</span>
                </div>
                <Icon name={config?.icon} size={16} className={config?.color} />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">{data?.count} products</span>
                  <span className={`font-medium ${config?.color}`}>{percentage}%</span>
                </div>
                <div className="w-full bg-muted rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${config?.bg} transition-all duration-300`}
                    style={{ width: `${Math.min(percentage, 100)}%` }}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Detailed Usage List */}
      <div className="space-y-4">
        {filteredData?.map((chemical) => {
          const config = getStatusConfig(chemical?.status);
          
          return (
            <div key={chemical?.id} className="p-4 border border-border rounded-lg hover:bg-muted/20 smooth-transition">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`p-2 rounded-lg ${config?.bgLight}`}>
                      <Icon name={getCategoryIcon(chemical?.category)} size={20} className={config?.color} />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground flex items-center space-x-2">
                        <span>{chemical?.name}</span>
                        {chemical?.organicApproved && (
                          <span className="px-2 py-1 bg-success/10 text-success text-xs rounded-full">
                            Organic
                          </span>
                        )}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span>EPA: {chemical?.epaNumber}</span>
                        <span className={getRestrictionColor(chemical?.restrictionLevel)}>
                          {chemical?.restrictionLevel} restriction
                        </span>
                        <span>{chemical?.applications} applications</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-6">
                  <div className="text-center lg:text-right">
                    <div className="text-sm text-muted-foreground">Usage</div>
                    <div className="font-medium text-foreground">
                      {chemical?.totalUsed} / {chemical?.maxAllowed} {chemical?.unit}
                    </div>
                    <div className="text-sm text-muted-foreground">
                      Last: {new Date(chemical.lastApplication)?.toLocaleDateString()}
                    </div>
                  </div>

                  <div className="flex flex-col items-center space-y-2">
                    <div className="relative w-16 h-16">
                      <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 100 100">
                        <circle
                          cx="50"
                          cy="50"
                          r="35"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="transparent"
                          className="text-muted/20"
                        />
                        <circle
                          cx="50"
                          cy="50"
                          r="35"
                          stroke="currentColor"
                          strokeWidth="8"
                          fill="transparent"
                          strokeDasharray={`${2 * Math.PI * 35}`}
                          strokeDashoffset={`${2 * Math.PI * 35 * (1 - chemical?.usagePercentage / 100)}`}
                          className={config?.color}
                          strokeLinecap="round"
                        />
                      </svg>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className={`text-sm font-semibold ${config?.color}`}>
                          {chemical?.usagePercentage}%
                        </span>
                      </div>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config?.bgLight} ${config?.color}`}>
                      {config?.icon === 'CheckCircle' ? 'Safe' : 
                       config?.icon === 'AlertTriangle' ? 'Monitor' : 'Limit'}
                    </span>
                  </div>
                </div>
              </div>
              {chemical?.usagePercentage >= 85 && (
                <div className={`mt-4 p-3 rounded-lg ${config?.bgLight} border-l-4 ${config?.bg?.replace('bg-', 'border-')}`}>
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={16} className={config?.color} />
                    <span className={`text-sm font-medium ${config?.color}`}>
                      {chemical?.usagePercentage >= 95 ? 'Critical: ' : 'Warning: '}
                      Approaching usage limit
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    Consider alternative products or adjust application schedule to stay within regulatory limits.
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChemicalUsageTracking;